import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

/**
 * Editorial 4-column footer.
 * - Brand block (left, wider) with mark, one-line positioning, JP+global note
 * - Product / Company / Resources / Legal — four columns of routes
 * - Footnote row with copyright, JP commercial-registry credit, language switcher
 *
 * All routes are real — no href="#" placeholders.
 */
export function SiteFooter() {
  const { t } = useTranslation();

  const product = [
    { to: '/pricing',  key: 'footer.product_pricing' },
    { to: '/compare',  key: 'footer.product_compare' },
    { to: '/demo',     key: 'footer.product_demo' },
    { to: '/status',   key: 'footer.product_status' },
    { to: '/changelog',key: 'footer.product_changelog' },
  ];

  const company = [
    { to: '/about',    key: 'footer.company_about' },
    { to: '/careers',  key: 'footer.company_careers' },
    { to: '/contact',  key: 'footer.company_contact' },
    { to: '/press',    key: 'footer.company_press' },
  ];

  const resources = [
    { to: '/trust',            key: 'footer.resources_trust' },
    { to: '/security',         key: 'footer.resources_security' },
    { to: '/subprocessors',    key: 'footer.resources_subprocessors' },
    { to: '/open-source',      key: 'footer.resources_open_source' },
  ];

  const legal = [
    { to: '/terms',           key: 'footer.legal_terms' },
    { to: '/privacy',         key: 'footer.legal_privacy' },
    { to: '/cookies',         key: 'footer.legal_cookies' },
    { to: '/dpa',             key: 'footer.legal_dpa' },
    { to: '/acceptable-use',  key: 'footer.legal_aup' },
    { to: '/tokusho',         key: 'footer.legal_tokusho' },
  ];

  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-ink-200 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-14 md:py-16">
        {/* Top — 4 columns */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 pr-2">
            <Link to="/" className="inline-flex items-center gap-2 text-ink-900 font-semibold">
              <BrandMark />
              <span className="serif-jp text-lg tracking-tight">{t('brand.name')}</span>
            </Link>
            <p className="mt-4 text-sm text-ink-600 leading-relaxed max-w-xs">
              {t('footer.brand_blurb')}
            </p>
            <p className="mt-4 text-xs text-ink-500 leading-relaxed max-w-xs">
              {t('footer.jp_global_note')}
            </p>
          </div>

          {/* Product */}
          <FooterCol titleKey="footer.col_product" items={product} t={t} />
          {/* Company */}
          <FooterCol titleKey="footer.col_company" items={company} t={t} />
          {/* Resources */}
          <FooterCol titleKey="footer.col_resources" items={resources} t={t} />
          {/* Legal */}
          <FooterCol titleKey="footer.col_legal" items={legal} t={t} />
        </div>

        {/* Footnote row */}
        <div className="mt-14 pt-6 border-t border-ink-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-[12.5px] text-ink-500 leading-relaxed max-w-2xl">
            <span className="num">{t('footer.copyright', { year })}</span>
            <span className="mx-2 text-ink-300">·</span>
            {t('footer.tagline_short')}
          </div>
          <div className="flex items-center gap-4">
            <p className="text-[11px] uppercase tracking-[0.16em] text-ink-400 hidden md:block">
              {t('common.language')}
            </p>
            <LanguageSwitcher />
          </div>
        </div>

        {/* JP commercial-registry sourcing credit (small print) */}
        <p className="mt-6 text-[11px] text-ink-400 leading-relaxed">
          {t('footer.jp_registry_credit')}
        </p>
      </div>
    </footer>
  );
}

function FooterCol({
  titleKey,
  items,
  t,
}: {
  titleKey: string;
  items: Array<{ to: string; key: string }>;
  t: (k: string) => string;
}) {
  return (
    <div className="md:col-span-2">
      <h3 className="text-[10.5px] uppercase tracking-[0.18em] text-ink-500 font-semibold">
        {t(titleKey)}
      </h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((it) => (
          <li key={it.to}>
            <Link
              to={it.to}
              className="text-[13.5px] text-ink-700 hover:text-accent-700 transition-colors"
            >
              {t(it.key)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BrandMark() {
  return <img src="/logo.png" alt="PatronEZ" className="h-8 w-8 rounded-lg object-contain shrink-0" />;
}
